import { msg } from '@lingui/core/macro'
import { styled } from '@mui/material'
import type { SubmitHandler } from 'react-hook-form'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'

import { useResolveLocalizable } from '@salsita-ai/platform-project-sdk/react'
import { TextInput } from '@salsita-ai/platform-project-sdk/ui'

import { useCustomerProperties } from '../shopify/web-component-properties.js'

export const FORM_ID = 'finalize-submit-button'

export const formSchema = z.object({
  emailAddress: z.string().email().min(1),
})

const Form = styled('form')(({ theme: { spacing } }) => ({
  display: 'grid',
  gap: spacing(2),
  paddingBlock: spacing(2),
}))

const emailPlaceholder = msg`Email address*`
const invalidEmailMessage = msg`Email address is invalid`
const requiredMessage = msg`This field is required`

interface AnonymousFinalizeFormInputs {
  emailAddress: string
}

export const FinalizeForm = ({ onSubmit: onSubmitCallback }: { onSubmit: (data: unknown) => void }) => {
  const resolveLocalizable = useResolveLocalizable()
  const { customerEmail } = useCustomerProperties()

  const { handleSubmit, control } = useForm<AnonymousFinalizeFormInputs>()

  const onSubmit: SubmitHandler<AnonymousFinalizeFormInputs> = data => {
    onSubmitCallback(data)
  }

  return (
    <Form id={FORM_ID} onSubmit={handleSubmit(onSubmit)}>
      <Controller
        name="emailAddress"
        defaultValue={customerEmail ?? ''}
        control={control}
        rules={{
          required: resolveLocalizable(requiredMessage),
          pattern: {
            value: /^[a-z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
            message: resolveLocalizable(invalidEmailMessage),
          },
        }}
        render={({ field, fieldState }) => (
          <TextInput
            type="email"
            placeholder={resolveLocalizable(emailPlaceholder)}
            hasError={!!fieldState.error}
            helperText={fieldState.error?.message ?? ' '}
            {...field}
          />
        )}
      />
    </Form>
  )
}
